import * as Yup from 'yup';
import { isNoTaxScheme, UAE_COUNTRY_CODE } from '../constants/defaults';
import { partyTaxSchemeShape } from './partyTaxSchemeValidation';
import { partyIdentificationFields } from './partyIdentificationValidation';

const buildBusinessProfileSchema = (messages = {}, options = {}) => {
  const m = messages?.business_profile ?? {};
  const { isEdit = false } = options;

  const partyTaxSchemeSchema = Yup.object()
    .shape(partyTaxSchemeShape(m))
    .notRequired()
    .nullable()
    .transform((value) => {
      if (!value) {
        return undefined;
      }

      const vat = value.company_id?.trim?.() ?? value.company_id;
      const scheme = value.tax_scheme;

      if ((!vat || vat === '') && (!scheme || isNoTaxScheme(scheme))) {
        return undefined;
      }

      return value;
    });

  const tinSchema = isEdit
    ? Yup.string().nullable()
    : Yup.string()
        .required(m.TIN_required || 'TIN is required')
        .trim()
        .length(10, m.TIN_length || 'TIN must be exactly 10 characters')
        .matches(/^1/, m.TIN_format || 'TIN must start with 1');

  return Yup.object().shape({
    // Basic Info
    name: Yup.string().required(m.name_required || 'Name is required'),
    party_name: Yup.string().required(m.party_name_required || 'Business Name is required'),
    party_name_in_local_language: Yup.string().nullable(),
    email: Yup.string()
      .required(m.email_required || 'Email is required')
      .email(m.email_invalid || 'Email must be a valid email')
      .max(320, m.email_max || 'Email must be at most 320 characters'),

    // Address
    postal_address: Yup.object().shape({
      street_name: Yup.string().required(m.street_name_required || 'Street Name is required'),
      additional_street_name: Yup.string().nullable(),
      address_line: Yup.string().nullable(),
      city_name: Yup.string().required(m.city_name_required || 'City Name is required'),
      country_code: Yup.object()
        .shape({
          code: Yup.string()
            .required(m.country_required || 'Country is required')
            .oneOf([UAE_COUNTRY_CODE], m.country_uae_only || 'Country must be United Arab Emirates (AE)'),
          label: Yup.string().required(),
        })
        .required(m.country_required || 'Country is required'),
      country_subentity: Yup.object()
        .shape({
          code: Yup.string().required(m.uae_subdivision_required || 'UAE Subdivision is required'),
          name: Yup.string().required(),
        })
        .required(m.uae_subdivision_required || 'UAE Subdivision is required')
        .nullable(),
      postal_zone: Yup.string().required(m.postal_zone_required || 'Postal Zone is required'),
    }),

    telephone: Yup.string().required(m.telephone_required || 'Telephone is required'),

    // Business Identifiers
    party_tax_scheme: partyTaxSchemeSchema,

    tin: tinSchema,

    scheme_agency_id: Yup.object()
      .shape({
        code: Yup.string().required(m.registration_type_required || 'Registration Type is required'),
        label: Yup.string().required(),
      })
      .required(m.registration_type_required || 'Registration Type is required'),

    company_id: Yup.string().required(m.registration_number_required || 'Registration Number is required'),

    authority_name: Yup.string()
      .nullable()
      .when('scheme_agency_id', {
        is: (val) => val?.code === 'TL',
        then: (schema) =>
          schema.required(
            m.registration_authority_required_trade_license ||
              'Registration Authority is required when Registration Type is Trade License',
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

    passport_issuing_country_code: Yup.mixed()
      .nullable()
      .when(['scheme_agency_id'], {
        is: (schemeAgencyId) => schemeAgencyId?.code === 'PAS',
        then: (schema) =>
          schema.required(m.passport_issuing_country_required || 'Passport Issuing Country is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    ...partyIdentificationFields(m),

    // Peppol fields
    participant_id: Yup.string().nullable(),
    peppol_participant: Yup.object().nullable(),

    logo_url: Yup.string().nullable(),
  });
};

// Backward-compatible default (English messages)
export const BusinessProfileValidation = buildBusinessProfileSchema();

// Preferred: pass `useTranslatedMessages().validations` and `{ isEdit: true }` on profile edit (TIN only)
export const BusinessProfileValidationSchema = (messages, options) => buildBusinessProfileSchema(messages, options);

export default BusinessProfileValidation;
